import { useExperimentsContext } from '../../experiments/components/ExperimentsContext'
import { EXPERIMENT_NAME_CART_INTERMEDIATE_DRAWER } from '../../experiments/model/EXPERIMENT_NAME'
import { userApiSwrRefetchMiniCart } from '../../user-data'
import { handleAddToCartErrorApm } from '../api/addToCartErrorApmLogger'
import {
  type CartIntermediateAddToCartProps,
  useCartIntermediateApiSwrProductAddToCartMutation,
} from '../api/swr/useCartIntermediateApiSwrProductAddToCartMutation'
import { cartIntermediateFreezeExpaCookie } from '../cookie-handling/cartIntermediateFreezeExpaCookie'
import { updateIsDataSuccessfullyAddedToCartFromResponse } from '../helpers/updateIsDataSuccessfullyAddedToCartFromResponse'
import { type CartIntermediateMutationResponseData } from '../model/CartIntermediateContextValue'
import { cartIntermediateAccessorGetDrawerMessages } from '../model/cartIntermediateMutationResponseDataAccessor'

import { useCartIntermediateContext } from './useCartIntermediateContext'

export const useProductAddToCart = ({
  currentPrescriptionType,
  offerId,
  product,
  quantity,
  redpoints,
  rxByPrincipleSelectedReason,
  sellerData,
}: CartIntermediateAddToCartProps): {
  handleAddToCart: () => Promise<void>
  isMutating: boolean
} => {
  const productAddToCartMutation =
    useCartIntermediateApiSwrProductAddToCartMutation({
      currentPrescriptionType,
      offerId,
      quantity,
      redpoints,
      rxByPrincipleSelectedReason,
    })
  const {
    openCartIntermediateDrawerWithData,
    resetCartIntermediateContext,
    setIsDataSuccessfullyAddedToCart,
    specialOffersCollapsibleControls: { handleClose: closeSpecialOffers },
  } = useCartIntermediateContext()
  const { addExperimentKeysForActivation, experiments } =
    useExperimentsContext()

  const handleAddToCart = async (): Promise<void> => {
    resetCartIntermediateContext()

    if (!product || !offerId) {
      return
    }

    const products = [
      {
        id: offerId,
        prescriptionType: currentPrescriptionType,
        product,
        redpoints,
        sellerData,
      },
    ]

    const handlePostAddToCart = (
      response: CartIntermediateMutationResponseData,
    ): void => {
      const drawerMessages = cartIntermediateAccessorGetDrawerMessages(response)
      updateIsDataSuccessfullyAddedToCartFromResponse(
        response,
        setIsDataSuccessfullyAddedToCart,
      )
      closeSpecialOffers()
      addExperimentKeysForActivation(EXPERIMENT_NAME_CART_INTERMEDIATE_DRAWER)
      cartIntermediateFreezeExpaCookie(experiments)
      openCartIntermediateDrawerWithData(products, drawerMessages, quantity)
    }

    const transaction = globalThis.clientSideApmInstance?.startTransaction(
      'Add to cart',
      'custom',
      { managed: true },
    )
    try {
      await productAddToCartMutation.trigger()
      await userApiSwrRefetchMiniCart()
    } catch (error) {
      handleAddToCartErrorApm(error)
    } finally {
      transaction?.end()
      const response = productAddToCartMutation.data ?? null
      handlePostAddToCart(response)
    }
  }

  return {
    handleAddToCart,
    isMutating: productAddToCartMutation.isMutating,
  }
}
